import { z } from 'zod';
import { communicationMetadataSchema } from '../common/metadata';

/**
 * Zod schemas for communication-related entities
 * These provide runtime validation and type inference
 */

// Message schema (API/Prisma may use createdAt instead of timestamp, read as 0/1)
const coerceTimestamp = (v: unknown): string =>
  v == null ? '' : typeof v === 'string' ? v : (v instanceof Date ? v.toISOString() : String(v));
const coerceBoolean = (v: unknown): boolean => v === true || v === 1 || v === '1';

export const messageSchema = z.object({
  id: z.string(),
  threadId: z.preprocess((v) => (v == null ? '' : v), z.string()),
  senderId: z.string(),
  senderName: z.string().optional(),
  recipientId: z.preprocess((v) => (v == null || v === '' ? '' : v), z.string()).optional(),
  recipientAddress: z.string().optional(),
  recipientName: z.string().optional(),
  content: z.preprocess((v) => (v == null ? '' : v), z.string()),
  timestamp: z.union([z.string(), z.date()]).optional(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  read: z.preprocess(coerceBoolean, z.boolean()),
  type: z.enum(['email', 'sms', 'system'] as const).optional(),
  attachments: z.array(z.string()).optional(),
  metadata: communicationMetadataSchema.optional(),
}).passthrough().transform((o) => {
  const raw = o as Record<string, unknown>;
  const rid = typeof o.recipientId === 'string' && o.recipientId.length > 0 ? o.recipientId : '';
  const raddr = typeof raw.recipientAddress === 'string' && raw.recipientAddress.length > 0 ? raw.recipientAddress : '';
  const mergedRecipient = rid || raddr;
  return {
    ...o,
    recipientId: mergedRecipient,
    recipientAddress: raddr || undefined,
    timestamp: coerceTimestamp(o.timestamp ?? raw.createdAt ?? ''),
  };
});

export type Message = z.infer<typeof messageSchema>;

// Thread schema: API/Prisma may use lastMessageAt, archived; allow component-used props
const coerceDate = (v: unknown): string =>
  v == null ? '' : typeof v === 'string' ? v : (v instanceof Date ? v.toISOString() : String(v));

export const threadSchema = z.object({
  id: z.string(),
  participants: z.array(z.string()),
  participantNames: z.array(z.string()).optional(),
  participantName: z.string().optional(),
  subject: z.preprocess((v) => (v == null ? '' : v), z.string()),
  lastMessagePreview: z.preprocess((v) => (v == null ? '' : v), z.string()),
  lastMessageDate: z.union([z.string(), z.date()]).optional(),
  lastMessageAt: z.union([z.string(), z.date()]).optional(),
  lastMessageTime: z.string().optional(),
  lastMessage: z.string().optional(),
  unreadCount: z.preprocess((v) => (v == null ? 0 : Number(v)), z.number().int().min(0)),
  category: z.string().optional().transform(c => (c && ['General', 'Maintenance', 'Leasing', 'Billing', 'Emergency'].includes(c) ? c as 'General' | 'Maintenance' | 'Leasing' | 'Billing' | 'Emergency' : 'General')),
  relatedEntityId: z.string().optional(),
  status: z.enum(['active', 'archived'] as const).optional(),
  archived: z.boolean().optional(),
}).passthrough().transform((o) => {
  const raw = o as Record<string, unknown>;
  const lastMessageDate = coerceDate(o.lastMessageDate ?? raw.lastMessageAt ?? '');
  const status = (raw.archived === true || raw.archived === 1 || o.status === 'archived') ? 'archived' as const : 'active' as const;
  const category = (o.category && ['General', 'Maintenance', 'Leasing', 'Billing', 'Emergency'].includes(o.category as string)) ? o.category : 'General';
  return { ...o, lastMessageDate, status, category };
});

export type Thread = z.infer<typeof threadSchema>;

// AppNotification schema
export const appNotificationSchema = z.object({
  id: z.string(),
  title: z.string().min(1, 'Notification title is required'),
  message: z.string().min(1, 'Notification message is required'),
  type: z.enum(['info', 'success', 'warning', 'error'] as const),
  timestamp: z.string(),
  read: z.boolean(),
  link: z.string().optional(),
  context: z.record(z.unknown()).optional(),
});

export type AppNotification = z.infer<typeof appNotificationSchema>;

// Notification schema (alias for AppNotification for consistency)
export const notificationSchema = appNotificationSchema;
export type Notification = z.infer<typeof notificationSchema>;

// EmailTemplate schema
export const emailTemplateSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Template name is required'),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Body is required'),
  html: z.string().optional(),
  variables: z.array(z.string()).optional(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EmailTemplate = z.infer<typeof emailTemplateSchema>;

// SMSTemplate schema
export const smsTemplateSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Template name is required'),
  message: z.string().min(1, 'Message is required'),
  body: z.string().optional(), // Alias for message
  variables: z.array(z.string()).optional(),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type SMSTemplate = z.infer<typeof smsTemplateSchema>;
// CommunicationTemplate schema
export const communicationTemplateSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Template name is required'),
  subject: z.string().min(1, 'Subject is required'),
  content: z.string().min(1, 'Content is required'),
  category: z.string().min(1, 'Category is required'),
  type: z.enum(['email', 'sms'] as const),
  variables: z.array(z.string()),
  active: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CommunicationTemplate = z.infer<typeof communicationTemplateSchema>;

/** Portal feed + community announcements (resident-facing). */
export const announcementSchema = z.object({
  id: z.string(),
  title: z.string(),
  content: z.string(),
  date: z.string(),
  author: z.string(),
  role: z.string(),
  avatar: z.string(),
  likes: z.number().int().nonnegative(),
  comments: z.number().int().nonnegative(),
  type: z.string().optional(),
});

export type AnnouncementDto = z.infer<typeof announcementSchema>;
